import pythonApiClient from './pythonBaseApi';

/**
 * Servicio para gestión de comensales
 * Maneja todas las operaciones CRUD relacionadas con comensales
 */

export const comensalApi = {
  /**
   * Obtiene comensales con filtros opcionales
   * @param {number|null} idProyecto - ID del proyecto (opcional)
   * @param {string|null} numDoc - Número de documento (opcional)
   * @param {boolean} soloActivos - Solo comensales activos (opcional)
   * @returns {Promise} - Respuesta de la API
   */
  getAll: (idProyecto = null, numDoc = null, soloActivos = false) => {
    let endpoint = '/mantenimientos/comensales';
    const params = new URLSearchParams();
    
    if (idProyecto) {
      params.append('id_proyecto', idProyecto);
    }
    if (numDoc) {
      params.append('num_doc', numDoc);
    }
    if (soloActivos) {
      params.append('solo_activos', '1');
    }
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }
    
    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.get(endpoint);
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // return apiClient.get(endpoint);
  },

  /**
   * Obtiene un comensal por ID
   * @param {number} id - ID del comensal
   * @returns {Promise} - Respuesta de la API
   */
  getById: (id) => {
    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.get(`/mantenimientos/comensales/${id}`);
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // return apiClient.get(`/comensales/${id}`);
  },

  /**
   * Crea un nuevo comensal
   * @param {Object} data - Datos del comensal
   * @param {string} data.nombre - Nombre completo del comensal
   * @param {string} data.tipo_documento_identidad - Tipo de documento de identidad
   * @param {string} data.numero_documento_identidad - Número de documento de identidad
   * @param {string} data.tipo_documento_comedor - Tipo de documento de comedor
   * @param {string} data.numero_documento_comedor - Número de documento de comedor
   * @param {string} data.cargo - Cargo del comensal
   * @param {string} data.referencia - Referencia adicional (opcional)
   * @param {string} data.empresa - Empresa del comensal (opcional)
   * @param {number} data.id_cliente - ID del cliente
   * @param {number} data.tipo_origen_trabajador_id - ID del tipo de origen (1=Local, 2=Foráneo)
   * @param {boolean} data.activo - Estado activo (default: true)
   * @param {Array<number>} data.comedores - Lista de IDs de comedores a asignar
   * @returns {Promise} - Respuesta de la API
   */
  create: (data) => {
    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.post('/mantenimientos/comensales', data);
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // return apiClient.post('/comensales', data);
  },

  /**
   * Actualiza un comensal existente
   * @param {number} id - ID del comensal
   * @param {Object} data - Datos actualizados del comensal
   * @returns {Promise} - Respuesta de la API
   */
  update: (id, data) => {
    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.put(`/mantenimientos/comensales/${id}`, data);
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // return apiClient.put(`/comensales/${id}`, data);
  },

  /**
   * Elimina un comensal
   * @param {number} id - ID del comensal
   * @returns {Promise} - Respuesta de la API
   */
  delete: (id) => {
    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.delete(`/mantenimientos/comensales/${id}`);
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // return apiClient.delete(`/comensales/${id}`);
  },

  /**
   * Obtiene comensales por proyecto
   * @param {number} proyectoId - ID del proyecto
   * @returns {Promise} - Respuesta de la API
   */
  getByProyecto: (proyectoId) => {
    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.get(`/mantenimientos/comensales?id_proyecto=${proyectoId}`);
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // return apiClient.get(`/comensales?id_proyecto=${proyectoId}`);
  },

  /**
   * Busca comensales por número de documento
   * @param {string} numDoc - Número de documento
   * @param {number} proyectoId - ID del proyecto
   * @returns {Promise} - Respuesta de la API
   */
  getByNumDoc: (numDoc, proyectoId) => {
    // Usar endpoint de Python (FastAPI) - mismo endpoint que getAll con filtros
    return pythonApiClient.get(`/mantenimientos/comensales?num_doc=${numDoc}&id_proyecto=${proyectoId}`);
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // return apiClient.get(`/comensales?num_doc=${numDoc}&id_proyecto=${proyectoId}`);
  },

  /**
   * Carga masiva de comensales (método anterior - JSON)
   * @param {Object} data - Datos para carga masiva
   * @param {Array} data.comensales - Array de comensales
   * @param {number} data.id_cliente - ID del cliente
   * @param {number} data.id_comedor - ID del comedor
   * @returns {Promise} - Respuesta de la API
   */
  bulkCreate: (data) => {
    // Usar endpoint de Python (FastAPI)
    // El backend espera un array de comensales directamente
    const comensales = data.comensales || [];
    return pythonApiClient.post('/mantenimientos/comensales/bulk', comensales);
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // return apiClient.post('/comensales/bulk', data);
  },

  /**
   * Valida archivo Excel antes de procesarlo (solo validación, sin procesar)
   * @param {File} file - Archivo Excel
   * @returns {Promise} - Respuesta de la API con resultado de validación
   */
  validateBulkUploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);

    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.post('/procesos/alimentacion/carga-masiva/comensales/validate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      validateStatus: (status) => {
        // Aceptar 200-299 (éxito) y 422 (validación con errores) como respuestas válidas
        return (status >= 200 && status < 300) || status === 422;
      }
    });
  },

  /**
   * Carga masiva de comensales desde archivo Excel
   * @param {File} file - Archivo Excel
   * @param {number} id_cliente - ID del cliente
   * @param {number} id_comedor - ID del comedor
   * @returns {Promise} - Respuesta de la API
   */
  bulkUpload: (file, id_cliente, id_comedor) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('id_cliente', id_cliente);
    formData.append('id_comedor', id_comedor);

    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.post('/procesos/alimentacion/carga-masiva/comensales', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      validateStatus: (status) => {
        // Aceptar 200-299 (éxito) y 422 (errores de validación por fila) como respuestas válidas. Esto permite que los errores detallados se visualicen
        return (status >= 200 && status < 300) || status === 422;
      }
    });
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // const formDataOld = new FormData();
    // formDataOld.append('excel_file', file);
    // formDataOld.append('id_cliente', id_cliente);
    // formDataOld.append('id_comedor', id_comedor);
    // return apiClient.post('/comensales/bulk-upload', formDataOld, {
    //   headers: {
    //     'Content-Type': 'multipart/form-data',
    //   },
    // });
  },

  /**
   * Valida el acceso de un comensal a un comedor específico
   * Verifica que el comensal exista, pertenezca al proyecto y tenga acceso al comedor
   * 
   * Usa el endpoint de Python (FastAPI) que también valida que el tipo de origen del comensal 
   * esté permitido para el tipo de servicio de alimentación
   * 
   * @param {string} numDoc - Número de documento del comensal
   * @param {number} proyectoId - ID del proyecto
   * @param {number} comedorId - ID del comedor
   * @param {number} tipoServicioAlimentacionId - ID del tipo de servicio de alimentación (requerido)
   * @returns {Promise} - Respuesta con datos del comensal y validación de acceso
   */
  validateAccess: async (numDoc, proyectoId, comedorId, tipoServicioAlimentacionId, fechaTurno = null) => {
    // Usar endpoint de Python (FastAPI) con cliente base compartido
    // validateStatus: 422 es una respuesta válida de validación, no debe rechazarse como error
    // tipoServicioAlimentacionId ya está validado en GestionarTurnos antes de abrir el modal
    let url = `/mantenimientos/comensales/validate-access?num_doc=${numDoc}&id_proyecto=${proyectoId}&id_comedor=${comedorId}&id_tipo_servicio_alimentacion=${tipoServicioAlimentacionId}`;
    if (fechaTurno) {
      url += `&fecha_turno=${fechaTurno}`;
    }
    
    return pythonApiClient.get(url, {
      validateStatus: (status) => {
        // Aceptar 200-299 (éxito) y 422 (validación) como respuestas válidas
        return (status >= 200 && status < 300) || status === 422;
      }
    });
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // const url = `/comensales/validate-access?num_doc=${numDoc}&id_proyecto=${proyectoId}&id_comedor=${comedorId}`;
    // return apiClient.get(url);
  },

  /**
   * Valida si un documento ya está registrado en un turno específico
   * @param {string} numDoc - Número de documento del comensal
   * @param {number} turnoId - ID del turno
   * @returns {Promise} - Respuesta con validación de duplicado
   */
  validateDocumentInTurnoApi: (numDoc, turnoId) => {
    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.get(`/mantenimientos/comensales/validate-document-in-turno?num_doc=${numDoc}&id_turno=${turnoId}`);
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // return apiClient.get(`/comensales/validate-document-in-turno?num_doc=${numDoc}&id_turno=${turnoId}`);
  },

  /**
   * Cambia el estado activo de un comensal
   * @param {number} id - ID del comensal
   * @param {boolean|null} activo - Estado específico (opcional, si no se envía hace toggle)
   * @returns {Promise} - Respuesta de la API
   */
  toggleActivo: (id, activo = null) => {
    const data = activo !== null ? { activo } : null;
    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.patch(`/mantenimientos/comensales/${id}/toggle-activo`, data);
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // const data = activo !== null ? { activo } : {};
    // return apiClient.patch(`/comensales/${id}/toggle-activo`, data);
  },

  /**
   * Busca comensales por número de documento de identidad para reportes
   * @param {string} numeroDocumento - Número de documento de identidad
   * @returns {Promise} - Respuesta con comensales encontrados
   */
  searchByNumeroDocumentoIdentidad: (numeroDocumento) => {
    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.get(`/mantenimientos/comensales/search-by-numero-documento-identidad?numero_documento=${numeroDocumento}`);
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // return apiClient.get(`/comensales/search-by-numero-documento-identidad?numero_documento=${numeroDocumento}`);
  },

  /**
   * Obtiene las atenciones/servicios de un comensal específico
   * @param {number} comensalId - ID del comensal
   * @returns {Promise} - Respuesta con atenciones del comensal
   */
  getAtencionesByComensalCliente: (comensalId) => {
    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.get(`/mantenimientos/comensales/atenciones-by-comensal-cliente?comensal_id=${comensalId}`);
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // return apiClient.get(`/comensales/atenciones-by-comensal-cliente?comensal_id=${comensalId}`);
  },

  /**
   * Obtiene dietas activas de comensales filtradas por proyecto
   * @param {Object} filtros - Filtros para la consulta
   * @param {number} filtros.id_proyecto - ID del proyecto (obligatorio)
   * @returns {Promise} - Respuesta con dietas activas filtradas
   */
  getDietasActivas: async (filtros = {}) => {
    if (!filtros.id_proyecto) {
      throw new Error('id_proyecto es obligatorio para obtener dietas activas');
    }
    
    const params = new URLSearchParams();
    params.append('id_proyecto', filtros.id_proyecto);
    
    console.log('🚀 FRONTEND: Iniciando petición getDietasActivas');
    const startTime = performance.now();
    
    try {
      // Usar endpoint de Python (FastAPI)
      const response = await pythonApiClient.get(`/mantenimientos/comensales/dietas-activas?${params.toString()}`);
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      console.log(`⚡ FRONTEND: getDietasActivas completado - Duración: ${duration.toFixed(2)}ms - Registros: ${response.data?.dietas?.length || 0}`);
      
      return response;
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      console.error(`❌ FRONTEND: getDietasActivas error - Duración: ${duration.toFixed(2)}ms - Error:`, error);
      throw error;
    }
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // const response = await apiClient.get(`/comensales/dietas-activas?${params.toString()}`);
  },

  // DEPRECATED: Mantener compatibilidad temporal
  searchByDocument: (numeroDocumento) => {
    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.get(`/mantenimientos/comensales/search-by-numero-documento-identidad?numero_documento=${numeroDocumento}`);
  },

  // DEPRECATED: Mantener compatibilidad temporal
  getAtencionesReporte: (comensalId) => {
    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.get(`/mantenimientos/comensales/atenciones-by-comensal-cliente?comensal_id=${comensalId}`);
  },

  /**
   * Activar o inactivar comensales masivamente desde archivo Excel
   * @param {File} file - Archivo Excel con una columna (documento de identidad)
   * @param {number} idCliente - ID del cliente
   * @param {string} accion - 'activar' o 'desactivar'
   * @returns {Promise} - Respuesta con resumen del proceso
   */
  activarInactivarMasivo: (file, idCliente, accion) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('id_cliente', idCliente);
    formData.append('accion', accion);
    
    // El proyecto se envía en el header X-Proyecto-ID automáticamente por pythonApiClient
    return pythonApiClient.post('/procesos/alimentacion/carga-masiva/activar-comensales/activar-inactivar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export default comensalApi;
